import { useState, useEffect, useRef, RefObject, Dispatch, SetStateAction } from 'react';

interface UseInfiniteScrollReturnType {
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  isScrollEnabled: boolean;
  setIsScrollEnabled: Dispatch<SetStateAction<boolean>>;
  searchPage: number;
  setSearchPage: Dispatch<SetStateAction<number>>;
  isSearchScrollEnabled: boolean;
  setIsSearchScrollEnabled: Dispatch<SetStateAction<boolean>>;
  isSearchScrollLoading: boolean;
  setIsSearchScrollLoading: Dispatch<SetStateAction<boolean>>;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
}

const useInfiniteScroll = (): UseInfiniteScrollReturnType => {
  const [page, setPage] = useState(0);
  const [searchPage, setSearchPage] = useState(0);
  const [isScrollEnabled, setIsScrollEnabled] = useState(false);
  const [isSearchScrollEnabled, setIsSearchScrollEnabled] = useState(false);
  const [isSearchScrollLoading, setIsSearchScrollLoading] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isThrottled = useRef(false);

  const loadThreshold = 0.8;
  const throttleDelay = 200; // ms

  const throttle = (callback: () => void, delay: number) => {
    if (isThrottled.current) return;
    isThrottled.current = true;
    callback();
    setTimeout(() => {
      isThrottled.current = false;
    }, delay);
  };

  useEffect(() => {
    const handleScroll = () => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const { scrollTop, clientHeight, scrollHeight } = container;
      const scrollPosition = scrollTop + clientHeight;
      const threshold = scrollHeight * loadThreshold;

      if (scrollPosition >= threshold) {
        throttle(() => {
          if (isSearchScrollEnabled && !isSearchScrollLoading) {
            setSearchPage((prev) => prev + 1);
            setIsSearchScrollLoading(true);
          } else if (isScrollEnabled) {
            setPage((prev) => prev + 1);
          }
        }, throttleDelay);
      }
    };

    const container = scrollContainerRef.current;
    if (container) container.addEventListener('scroll', handleScroll);

    return () => {
      if (container) container.removeEventListener('scroll', handleScroll);
    };
  }, [isScrollEnabled, isSearchScrollEnabled, isSearchScrollLoading]);

  useEffect(() => {
    if (!isScrollEnabled) return;
    // Use a short timeout to allow the DOM to update with newly rendered items
    // before measuring, so we don't immediately blast through all pages.
    const timer = setTimeout(() => {
      const container = scrollContainerRef.current;
      if (!container) return;
      if (container.scrollHeight <= container.clientHeight) {
        setPage((prev) => prev + 1);
      }
    }, 300);
    return () => clearTimeout(timer);
    // Intentionally omit `page` from deps — we only want this to fire when
    // isScrollEnabled transitions to true, not after every page increment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScrollEnabled]);

  return {
    page,
    isScrollEnabled,
    setIsScrollEnabled,
    searchPage,
    setIsSearchScrollEnabled,
    isSearchScrollEnabled,
    isSearchScrollLoading,
    setIsSearchScrollLoading,
    setPage,
    setSearchPage,
    scrollContainerRef,
  };
};

export default useInfiniteScroll;
